/**
 * AnalysisCombinedView - Displays all selected measurements on a single chart.
 *
 * Supports two modes:
 * - Normalized (default): Values shown as percentage deviation from their mean,
 *   revealing relative volatility. Stable parameters stay flat near 0%,
 *   while volatile parameters show wider swings.
 * - Raw: Values plotted as-is without transformation. Useful for comparing
 *   the same parameter from different data sources on one chart.
 *
 * The legend shows parameter names with their original units.
 *
 * The tank's target range is drawn as a green band only when every series is
 * the same parameter. With Phosphate and Nitrate together there is no band,
 * because one band can't describe both.
 */
import { useMemo, useState } from 'react';
import { Paper, Alert, Stack, Group, ActionIcon, Tooltip as MantineTooltip } from '@mantine/core';
import { IconAlertCircle, IconNote } from '@tabler/icons-react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { format } from 'date-fns';
import {
  TooltipContainer,
  TooltipHeader,
  TooltipRow,
  TooltipNote,
  formatMeasurementValue,
} from './components/ChartTooltip';
import type { MeasurementPoint, DosingPoint, TaskEvent } from '@/hooks/useAnalysisData';
import type { TankTargetRange } from '@/hooks/useTargetRanges';
import type { ChartAnnotation } from '@/types/chartAnnotation';
import { AnalysisEmptyState } from './AnalysisEmptyState';
import { AnalysisDosingChart } from './AnalysisDosingChart';
import { AnalysisEventsRow } from './AnalysisEventsRow';
import { AnalysisEventLines } from './AnalysisEventLines';
import { ChartAnnotationLayer } from '@/components/charts/ChartAnnotationLayer';
import {
  CHART_COLORS,
  getParameterConfig,
  parseParameterKey,
  formatParameterLabel,
  getSourceDisplayName,
  SPARSE_DATA_SOURCES,
} from './constants';
import { CHART_CROSSHAIR_COLOR, TARGET_RANGE_BAND_COLOR } from '@/utils/chartColors';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { computeCenteredMovingAverage } from './utils/movingAverage';
import { resolveTargetBand, type TargetBand } from './utils/targetBand';

/** Suffix used for the faint raw-overlay series keys revealed on legend focus. */
const RAW_KEY_SUFFIX = '__raw';

/** Height of the combined chart in pixels */
const CHART_HEIGHT = 400;

interface AnalysisCombinedViewProps {
  /** List of selected parameter keys to display */
  selectedParameters: string[];
  /** Measurement data grouped by parameter key */
  measurements: Record<string, MeasurementPoint[]>;
  /** List of selected dosing chemical names to display */
  selectedDosing?: string[];
  /** Dosing data grouped by chemical name */
  dosing?: Record<string, DosingPoint[]>;
  /** List of selected event type names to display */
  selectedEvents?: string[];
  /** All available maintenance events */
  events?: TaskEvent[];
  /** Date range for event positioning (YYYY-MM-DD format) */
  dateRange?: {
    start: string;
    end: string;
  };
  /** Whether to show vertical event lines on charts */
  showEventLines?: boolean;
  /** Whether to normalize values as % deviation from mean (default: true) */
  normalized?: boolean;
  /** Moving-average window length in ms (null = no smoothing). */
  smoothingWindowMs?: number | null;
  /** Tank target ranges; drawn as a band only when all series share one parameter. */
  targetRanges?: TankTargetRange[];
  /** Chart annotations to display */
  annotations?: ChartAnnotation[];
  /** Callback when "Add Note" button is clicked */
  onAddNote?: (parameter: string, parameterLabel: string, sources?: string[]) => void;
  /** Callback when an annotation is clicked */
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}

interface ChartDataPoint {
  timestamp: number;
  [key: string]: number; // percentage deviation from mean, keyed by paramKey
}

interface ParameterStats {
  mean: number;
  hasData: boolean;
}

/**
 * Get legend name for a parameter (label + unit).
 * Handles composite keys (param:source) by parsing and formatting appropriately.
 */
function getLegendName(compositeKey: string): string {
  const { param } = parseParameterKey(compositeKey);
  const config = getParameterConfig(param);
  const label = formatParameterLabel(compositeKey);

  if (config.unit) {
    return `${label} (${config.unit})`;
  }
  return label;
}

/**
 * Calculate mean value for a parameter.
 */
function calculateStats(points: MeasurementPoint[]): ParameterStats {
  if (!points || points.length === 0) {
    return { mean: 0, hasData: false };
  }

  const values = points.map((p) => p.value);
  const sum = values.reduce((acc, val) => acc + val, 0);
  return {
    mean: sum / values.length,
    hasData: true,
  };
}

/**
 * Calculate percentage deviation from mean.
 * Returns 0 if mean is 0 (avoids division by zero).
 */
function normalizeValue(value: number, mean: number): number {
  if (mean === 0) {
    return 0;
  }
  return ((value - mean) / mean) * 100;
}

/**
 * Build the combined chart data for all parameters in a single pass.
 *
 * For each series the raw value is optionally normalized (% deviation from the
 * RAW-series mean held in `statsMap`) and then, for dense series with smoothing
 * enabled, replaced by a centered moving average. Because normalization is a
 * linear transform, normalizing-then-smoothing is mathematically identical to
 * smoothing-then-normalizing, so `statsMap` MUST stay computed from the raw
 * measurements and never be recomputed on smoothed values.
 *
 * Sparse series (manual/icp) are never smoothed; their primary line stays raw.
 *
 * When a dense series is smoothed, its primary key (`paramKey`) holds the
 * smoothed values and a parallel raw overlay is stored under
 * `${paramKey}${RAW_KEY_SUFFIX}` so it can be revealed on legend focus. The set
 * of smoothed keys is returned so the component knows which raw overlays exist.
 */
function buildCombinedChartData(
  parametersWithData: string[],
  measurements: Record<string, MeasurementPoint[]>,
  statsMap: Map<string, ParameterStats>,
  normalized: boolean,
  smoothingWindowMs: number | null
): { chartData: ChartDataPoint[]; smoothedKeys: Set<string> } {
  const timestampMap = new Map<number, ChartDataPoint>();
  const smoothedKeys = new Set<string>();

  const upsert = (ts: number, key: string, value: number): void => {
    let dp = timestampMap.get(ts);
    if (!dp) {
      dp = { timestamp: ts };
      timestampMap.set(ts, dp);
    }
    dp[key] = value;
  };

  parametersWithData.forEach((paramKey) => {
    const stats = statsMap.get(paramKey);
    if (!stats?.hasData) return;
    const { source } = parseParameterKey(paramKey);
    const isSparse = source ? SPARSE_DATA_SOURCES.has(source) : false;

    const series = (measurements[paramKey] || [])
      .map((p) => ({
        timestamp: new Date(p.timestamp).getTime(),
        value: normalized ? normalizeValue(p.value, stats.mean) : p.value,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    const doSmooth = smoothingWindowMs != null && !isSparse;
    if (doSmooth) {
      smoothedKeys.add(paramKey);
      // Raw overlay (revealed on focus) under `${paramKey}${RAW_KEY_SUFFIX}`.
      series.forEach((p) => upsert(p.timestamp, `${paramKey}${RAW_KEY_SUFFIX}`, p.value));
      computeCenteredMovingAverage(series, smoothingWindowMs).forEach((p) =>
        upsert(p.timestamp, paramKey, p.value)
      );
    } else {
      series.forEach((p) => upsert(p.timestamp, paramKey, p.value));
    }
  });

  return {
    chartData: Array.from(timestampMap.values()).sort((a, b) => a.timestamp - b.timestamp),
    smoothedKeys,
  };
}

/**
 * Custom tooltip formatter that shows original values alongside normalized percentages.
 * Handles composite keys (param:source) by parsing for config lookup.
 */
export function CustomTooltip({
  active,
  payload,
  label,
  measurements,
  normalized,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; stroke: string }>;
  label?: number;
  measurements: Record<string, MeasurementPoint[]>;
  normalized: boolean;
}): JSX.Element | null {
  if (!active || !payload || payload.length === 0 || !label) {
    return null;
  }

  return (
    <TooltipContainer>
      <TooltipHeader timestamp={label} showTime />
      {payload.map((entry) => {
        // Skip the faint raw-overlay series injected when a smoothed line is focused;
        // it shares the smoothed series' label and would render a duplicate/broken row.
        if (entry.dataKey.endsWith(RAW_KEY_SUFFIX)) return null;
        const compositeKey = entry.dataKey;
        const { param } = parseParameterKey(compositeKey);
        const config = getParameterConfig(param);
        const displayLabel = formatParameterLabel(compositeKey);

        // Find the original value and device at this timestamp
        const points = measurements[compositeKey] || [];
        const originalPoint = points.find(
          (p) => new Date(p.timestamp).getTime() === label
        );
        const device = originalPoint?.device;

        let formattedValue: string;
        if (normalized) {
          const originalValue = originalPoint?.value;
          const rawFormatted = originalValue !== undefined
            ? formatMeasurementValue(originalValue, param)
            : '-';
          formattedValue = `${rawFormatted} (${entry.value >= 0 ? '+' : ''}${entry.value.toFixed(1)}%)`;
        } else {
          formattedValue = formatMeasurementValue(entry.value, param);
        }

        return (
          <div key={compositeKey}>
            <TooltipRow
              color={entry.stroke}
              label={displayLabel}
              value={formattedValue}
              unit={config.unit}
              subtitle={device ? `Source: ${getSourceDisplayName(device)}` : undefined}
            />
            {originalPoint?.note && <TooltipNote note={originalPoint.note} />}
          </div>
        );
      })}
    </TooltipContainer>
  );
}

/**
 * Combined view showing all selected parameters as normalized lines on a single chart.
 * Also displays dosing chart and events row below measurements when items are selected.
 */
export function AnalysisCombinedView({
  selectedParameters,
  measurements,
  selectedDosing = [],
  dosing = {},
  selectedEvents = [],
  events = [],
  dateRange,
  showEventLines = false,
  normalized = true,
  smoothingWindowMs = null,
  targetRanges,
  annotations,
  onAddNote,
  onAnnotationClick,
}: AnalysisCombinedViewProps): JSX.Element {
  // Parameter key whose faint raw overlay is currently revealed (via legend hover).
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  // Compute time domain from date range for chart alignment
  const timeDomain = useMemo(() => {
    if (!dateRange) return undefined;
    return {
      startTs: new Date(`${dateRange.start}T00:00:00`).getTime(),
      endTs: new Date(`${dateRange.end}T23:59:59.999`).getTime(),
    };
  }, [dateRange]);

  // Calculate stats for each parameter
  // Note: All hooks must be called before any early returns
  const statsMap = useMemo(() => {
    const map = new Map<string, ParameterStats>();
    selectedParameters.forEach((paramKey) => {
      const points = measurements[paramKey] || [];
      map.set(paramKey, calculateStats(points));
    });
    return map;
  }, [selectedParameters, measurements]);

  // Get parameters that have data
  const parametersWithData = useMemo(
    () => selectedParameters.filter((paramKey) => statsMap.get(paramKey)?.hasData),
    [selectedParameters, statsMap]
  );

  // Filter annotations for selected parameters (match param portion of composite keys)
  const filteredAnnotations = useMemo(() => {
    if (!annotations) return [];
    const paramNames = parametersWithData.map((key) => parseParameterKey(key).param);
    return annotations.filter(
      (a) => !a.parameter || paramNames.includes(a.parameter)
    );
  }, [annotations, parametersWithData]);

  // Transform data for the chart (normalize + optional smoothing in a single pass)
  const { chartData, smoothedKeys } = useMemo(
    () => buildCombinedChartData(parametersWithData, measurements, statsMap, normalized, smoothingWindowMs),
    [parametersWithData, measurements, statsMap, normalized, smoothingWindowMs]
  );

  // Map each line's legend label back to its parameter key. Recharts' Legend
  // payload reliably exposes `value` (the line `name`) but not `dataKey`.
  const legendNameToKey = useMemo(() => {
    const map = new Map<string, string>();
    parametersWithData.forEach((key) => map.set(getLegendName(key), key));
    return map;
  }, [parametersWithData]);

  // Target band in plotted units. Normalized values are % deviation from each
  // series' own mean, so with two sources of the same parameter the band would
  // land in a different place for each; only a single series gets a band there.
  const targetBand = useMemo((): TargetBand | null => {
    const band = resolveTargetBand(parametersWithData, targetRanges);
    if (!band || !normalized) return band;
    if (parametersWithData.length !== 1) return null;
    const mean = statsMap.get(parametersWithData[0]!)?.mean;
    if (!mean) return null;
    const a = normalizeValue(band.min, mean);
    const b = normalizeValue(band.max, mean);
    return { min: Math.min(a, b), max: Math.max(a, b) };
  }, [parametersWithData, targetRanges, normalized, statsMap]);

  // Calculate Y-axis domain based on chart data (and the target band, so it is
  // never clipped off when the data sits entirely inside or outside it)
  const yAxisDomain = useMemo(() => {
    if (chartData.length === 0) return normalized ? [-10, 10] : [0, 10];

    if (normalized) {
      // Symmetric domain centered on 0% for normalized view
      let maxAbsDeviation = targetBand
        ? Math.max(Math.abs(targetBand.min), Math.abs(targetBand.max))
        : 0;
      chartData.forEach((point) => {
        parametersWithData.forEach((paramKey) => {
          const value = point[paramKey];
          if (typeof value === 'number') {
            maxAbsDeviation = Math.max(maxAbsDeviation, Math.abs(value));
          }
        });
      });

      const padding = Math.max(maxAbsDeviation * 0.1, 1);
      const maxValue = maxAbsDeviation + padding;
      const niceMax = Math.ceil(maxValue / 5) * 5;
      return [-niceMax, niceMax];
    }

    // Raw mode: auto-scale based on actual min/max values
    let minVal = targetBand?.min ?? Infinity;
    let maxVal = targetBand?.max ?? -Infinity;
    chartData.forEach((point) => {
      parametersWithData.forEach((paramKey) => {
        const value = point[paramKey];
        if (typeof value === 'number') {
          minVal = Math.min(minVal, value);
          maxVal = Math.max(maxVal, value);
        }
      });
    });

    if (minVal === Infinity || maxVal === -Infinity) return [0, 10];

    const range = maxVal - minVal;
    const padding = Math.max(range * 0.1, 0.1);
    return [minVal - padding, maxVal + padding];
  }, [normalized, chartData, parametersWithData, targetBand]);

  // Resolve the faint raw-overlay for the focused smoothed series (revealed on
  // legend hover). null when nothing is focused or the focused series isn't
  // smoothed, so the overlay <Line> is omitted entirely.
  const focusedRawOverlay = useMemo<{ dataKey: string; color: string } | null>(() => {
    if (!focusedKey || !smoothedKeys.has(focusedKey)) return null;
    const focusedIndex = parametersWithData.indexOf(focusedKey);
    if (focusedIndex < 0) return null;
    const color = CHART_COLORS[focusedIndex % CHART_COLORS.length] ?? CHART_COLORS[0];
    return { dataKey: `${focusedKey}${RAW_KEY_SUFFIX}`, color };
  }, [focusedKey, smoothedKeys, parametersWithData]);

  // Show empty state when no parameters, dosing, or events selected
  if (selectedParameters.length === 0 && selectedDosing.length === 0 && selectedEvents.length === 0) {
    return <AnalysisEmptyState />;
  }

  // Show message if no parameters have data and only parameters are selected (no dosing or events)
  if (parametersWithData.length === 0 && selectedDosing.length === 0 && selectedEvents.length === 0) {
    return (
      <Paper p="xl" withBorder radius="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="gray"
          variant="light"
          title="No data available"
        >
          The selected parameters have no data in the current time range.
        </Alert>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {/* Measurement chart - only show if there are parameters with data */}
      {parametersWithData.length > 0 && (
        <Paper p="md" withBorder radius="md" style={{ overflow: 'hidden' }}>
          {/* Chart header with Add Note button */}
          {onAddNote && (
            <Group justify="flex-end" mb="xs">
              <MantineTooltip label="Add note" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => {
                    // Pass composite keys so the modal can show a series picker
                    onAddNote('', 'Combined View', parametersWithData);
                  }}
                  aria-label="Add note to chart"
                >
                  <IconNote size={16} />
                </ActionIcon>
              </MantineTooltip>
            </Group>
          )}
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={timeDomain ? [timeDomain.startTs, timeDomain.endTs] : ['dataMin', 'dataMax']}
                tickFormatter={(value) => format(new Date(value), 'MMM d')}
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={yAxisDomain}
                tickFormatter={(value: number) => {
                  if (normalized) {
                    if (value === 0) return '0%';
                    return `${value > 0 ? '+' : ''}${value.toFixed(0)}%`;
                  }
                  // Raw mode: format as plain number
                  if (Number.isInteger(value) || Math.abs(value) >= 100) {
                    return value.toFixed(0);
                  }
                  return value.toFixed(1);
                }}
                tick={CHART_AXIS_TICK}
                width={50}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    measurements={measurements}
                    normalized={normalized}
                  />
                }
                cursor={{ stroke: CHART_CROSSHAIR_COLOR, strokeWidth: 1, strokeDasharray: '4 4' }}
              />
              <Legend
                verticalAlign="top"
                height={36}
                wrapperStyle={CHART_LEGEND_STYLE}
                onMouseEnter={(o) => setFocusedKey(legendNameToKey.get(String(o.value)) ?? null)}
                onMouseLeave={() => setFocusedKey(null)}
              />
              {/* Target range band, rendered before the lines so it sits behind them */}
              {targetBand && (
                <ReferenceArea
                  y1={targetBand.min}
                  y2={targetBand.max}
                  fill={TARGET_RANGE_BAND_COLOR}
                  fillOpacity={0.08}
                  stroke={TARGET_RANGE_BAND_COLOR}
                  strokeOpacity={0.2}
                  strokeDasharray="3 3"
                />
              )}
              {parametersWithData.map((paramKey, index) => {
                const { source } = parseParameterKey(paramKey);
                const isSparse = source ? SPARSE_DATA_SOURCES.has(source) : false;
                const color = CHART_COLORS[index % CHART_COLORS.length];
                return (
                  <Line
                    key={paramKey}
                    type="monotone"
                    dataKey={paramKey}
                    name={getLegendName(paramKey)}
                    stroke={color}
                    strokeWidth={1.5}
                    dot={isSparse
                      ? { r: 3, fill: color, stroke: '#fff', strokeWidth: 1.5 }
                      : { r: 2, fill: color }
                    }
                    activeDot={{ r: isSparse ? 5 : 4 }}
                    connectNulls
                  />
                );
              })}
              {/* Faint raw overlay for the focused smoothed series (revealed on legend hover) */}
              {focusedRawOverlay && (
                <Line
                  type="monotone"
                  dataKey={focusedRawOverlay.dataKey}
                  stroke={focusedRawOverlay.color}
                  strokeWidth={1}
                  strokeOpacity={0.25}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  legendType="none"
                  connectNulls
                />
              )}
              {/* Annotation overlays */}
              <ChartAnnotationLayer
                annotations={filteredAnnotations}
                onAnnotationClick={onAnnotationClick}
              />
              {/* Event lines using shared component */}
              {dateRange && (
                <AnalysisEventLines
                  selectedEvents={selectedEvents}
                  events={events}
                  dateRange={dateRange}
                  showEventLines={showEventLines}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Paper>
      )}
      {/* Dosing chart shown below measurement chart when dosing items are selected */}
      <AnalysisDosingChart
        selectedDosing={selectedDosing}
        dosing={dosing}
        timeDomain={timeDomain}
        selectedEvents={selectedEvents}
        events={events}
        dateRange={dateRange}
        showEventLines={showEventLines}
      />
      {/* Events row shown below dosing chart when events are selected */}
      {dateRange && (
        <AnalysisEventsRow
          selectedEvents={selectedEvents}
          events={events}
          dateRange={dateRange}
        />
      )}
    </Stack>
  );
}
